import { useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

export function getVideoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{5,})/);
  if (yt) return { type: "embed", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "embed", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { type: "file", src: url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url };
}

export const VideoPlayer = ({ url, className = "" }) => {
  const emb = getVideoEmbed(url);
  if (!emb) return null;
  if (emb.type === "embed") {
    return (
      <div className={`aspect-video w-full overflow-hidden ${className}`} data-testid="video-embed">
        <iframe
          src={emb.src} title="Exercise demo" className="w-full h-full" style={{ border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video src={emb.src} controls preload="metadata" playsInline
      className={`aspect-video w-full bg-black ${className}`} data-testid="video-file" />
  );
};

const CHUNK = 4 * 1024 * 1024;

export const VideoUploader = ({ onUploaded }) => {
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setProgress(0);
      const { data: start } = await api.post("/upload/start", {
        filename: file.name, content_type: file.type || "video/mp4",
      });
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post(`/upload/chunk/${start.upload_id}`, fd);
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const { data: fin } = await api.post(`/upload/finish/${start.upload_id}`);
      setProgress(100);
      onUploaded(fin.url);
      toast.success("Video uploaded");
    } catch {
      toast.error("Upload failed — please try again");
    } finally {
      setTimeout(() => setProgress(null), 900);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={pick} data-testid="video-file-input" />
      <button type="button" className="btn-secondary text-xs" disabled={progress !== null}
        onClick={() => inputRef.current?.click()} data-testid="video-upload-btn">
        {progress !== null ? <><Loader2 size={13} className="animate-spin" /> Uploading {progress}%</> : <><Upload size={13} /> Upload video file</>}
      </button>
      {progress !== null && (
        <div className="mt-2 h-1 w-full" style={{ background: "var(--border)" }}>
          <div className="h-1 transition-all" style={{ width: `${progress}%`, background: "var(--accent)" }} />
        </div>
      )}
    </div>
  );
};
